export const patientListDocumentation = `
## Table Columns

| Column | Width | Filter Type | Permission Required | Hideable | Description |
|--------|-------|-------------|-------------------|----------|-------------|
| Account Custom Tags | 270px | Select (bubbles) | ACCOUNTS_GET (for DS account) | <span class="checkmark">✓</span> | Account-level custom tags displayed as colored bubbles |
| Account | 220px | Select with dynamic hint | ACCOUNTS_GET (for DS account) | <span class="crossmark">✗</span> | Business name of the account (not hideable) |
| Custom Tags | 200px | Select (bubbles) | ACCOUNTS_GET (for DS account) | <span class="checkmark">✓</span> | Patient-level custom tags displayed as colored bubbles |
| Custom ID | 200px | Text | ACCOUNTS_GET (for DS account) | <span class="checkmark">✓</span> | Account custom identifier |
| MRN Number | 200px | Text | - | <span class="checkmark">✓</span> | Medical Record Number |
| First Name | 200px | Text | - | <span class="checkmark">✓</span> | Patient's first name |
| Last Name | 200px | Text | - | <span class="checkmark">✓</span> | Patient's last name |
| Date of Birth | 200px | Date range | - | <span class="checkmark">✓</span> | Patient's date of birth (localized format) |

## Account Type Dependencies

### Distribution Sponsors
- See additional account-related columns when viewing patients across multiple accounts
- Different table key used: \`patientsDSTable\`
- Cannot add new patients when viewing all accounts (button hidden)
- Permission checks differ for child account patients vs current account

### Regular Accounts
- Table key: \`patientsTable\`
- Standard column visibility based on permissions
- Full patient management capabilities within their account

## Row Actions

| Action | Icon | Permission Required | Description |
|--------|------|-------------------|-------------|
| Create Order | Add | ORDER_CREATE | Creates a new order for the selected patient. Permission checked against current or child account based on patient ownership. |
| Create Assessment | Add | ORDER_SURVEY_CREATE | Creates a new assessment/survey for the patient. Redirects to assessment creation page. |
| Edit Patient | Edit | PATIENT_PATCH | Opens patient edit modal for updating patient information. Always available for owned patients. |
| Remove Patient | Remove | PATIENT_DELETE | Deletes the patient after confirmation dialog. Permanently removes patient data. |

## Filtering and Search

### Text Filters
- **First Name**: Include, exact match, or starts-with filtering
- **Last Name**: Include, exact match, or starts-with filtering
- **MRN Number**: Include, exact match, or starts-with filtering
- **Custom ID**: Include, exact match, or starts-with filtering

### Select Filters
- **Account Custom Tags**: Predefined options from \`CUSTOM_TAGS_OPTIONS\`
- **Custom Tags**: Options from \`PATIENT_CUSTOM_TAGS_OPTIONS\`
- **Account**: Dynamic options loaded from API based on accessible accounts

### Date Filters
- **Date of Birth**: Date range picker with localized date formatting
- Supports from/to date selection

### Filter Persistence
- Filters stored in sessionStorage with daily expiration
- Account-specific storage using hashed account UUID
- Maintains filter state across browser sessions

## Table Features

### Virtual Scrolling
- Uses react-virtualized for performance with large datasets
- Dynamic page size calculation: \`parseInt(window.innerHeight / 60) + 20\`
- Infinite scroll with automatic loading of additional pages
- Loading shimmer effects during data fetch

### Column Management
- **Sortable**: All columns support sorting (default: lastName ascending)
- **Resizable**: Users can adjust column widths
- **Reorderable**: Drag and drop column reordering
- **Pinnable**: Pin important columns to left or right
- **Hideable**: Most columns can be hidden (except Account column)

### Data Display
- Empty values display as "-" or "N/A" as appropriate
- Custom tags shown as colored bubbles with sort relevance
- Dates displayed in localized format
- Account identifiers fallback to "N/A" if not set

## Permissions

### Required for Table Access
- **PATIENTS_GET**: Basic permission to view patient list

### Column-Specific Permissions
- **ACCOUNTS_GET**: Required for account-related columns (Account Custom Tags, Account, Custom Tags, Custom ID) - indicates Distribution Sponsor account type

### Action Permissions
- **PATIENT_CREATE**: Create new patients (Add Patient button)
- **PATIENT_PATCH**: Edit existing patients
- **PATIENT_DELETE**: Remove patients
- **ORDER_CREATE**: Create orders for patients
- **ORDER_SURVEY_CREATE**: Create assessments for patients

### Permission Context
For Distribution Sponsors viewing child account patients:
- Permission checks use \`childAccountPermissions\`
- Actions may be restricted based on child account permission set
- Failed permission attempts show warning notifications

## API Endpoints

| Method | Endpoint | Description | Parameters |
|--------|----------|-------------|------------|
| GET | \`/accounts/{accountUuid}/patients\` | Fetch patient list | fields, pagination, filters, sorting |
| DELETE | \`/accounts/{accountUuid}/patients/{patientUuid}\` | Remove patient | - |
| GET | \`/accounts/{accountUuid}/patients/{patientUuid}\` | Get patient details | - |

### Query Parameters
- **fields**: Comma-separated list of columns to retrieve
- **page**: Page number for pagination
- **limit**: Number of items per page
- **sort**: Sorting specification
- **filter**: Applied filter parameters

## InfoPane Integration

Clicking on certain data cells opens detailed patient information:

### Clickable Cells
- MRN Number
- First Name
- Last Name
- Date of Birth

### InfoPane Content
- Complete patient profile information
- Contact details and preferences
- Account associations
- Quick action buttons for patient management
- Navigation to detailed patient view


## Table Personalization

### Persistent Settings
Settings are stored in Redux and persist across sessions:

- **Column Visibility**: Which columns are shown/hidden
- **Column Order**: Custom arrangement of columns
- **Column Sizing**: User-adjusted column widths
- **Column Pinning**: Pinned column positions
- **Sort Preferences**: Default sorting configuration
- **Filter Preferences**: Recently used filter combinations

### Default Configuration
- **Default Sort**: Last Name (ascending)
- **Default Columns**: All columns visible except those requiring missing permissions
- **Default Page Size**: Dynamic based on viewport height
- **Default Filters**: None applied

## Loading States

### Data Loading
- Shimmer effects during initial load and pagination
- Loading indicators for filter application
- Progress indicators for bulk operations

### Error Handling
- Network error notifications
- Permission error warnings
- Data validation error messages
- Retry mechanisms for failed requests

## Search and Navigation

### Quick Search
- Global search across key patient fields
- Real-time results as user types
- Search history for repeated queries

### Navigation Actions
- Click patient data to open InfoPane
- Double-click to navigate to patient detail page
- Keyboard navigation support for accessibility
- Breadcrumb navigation for context awareness
`;
